import React, { useState, useRef, useEffect } from "react";
import monsterData from "./data/monsterData.json";
import lootData from "./data/lootData.json";
import environmentData from "./data/environmentData.json";

const gridWidth = 120;
const gridHeight = 60;
const scale = 16;
const canvasSize = {
  width: gridWidth * scale,
  height: gridHeight * scale,
};

const roomSizes = [
  { width: 4, height: 8, monsterDensity: 2, environmentDensity: 1 },
  { width: 8, height: 4, monsterDensity: 2, environmentDensity: 1 },
  { width: 8, height: 8, monsterDensity: 2, environmentDensity: 2 },
  { width: 12, height: 8, monsterDensity: 3, environmentDensity: 2 },
  { width: 8, height: 12, monsterDensity: 3, environmentDensity: 2 },
  { width: 12, height: 12, monsterDensity: 4, environmentDensity: 2 },
  { width: 16, height: 12, monsterDensity: 5, environmentDensity: 2 },
  { width: 12, height: 16, monsterDensity: 5, environmentDensity: 2 },
  { width: 8, height: 16, monsterDensity: 3, environmentDensity: 2 },
  { width: 16, height: 8, monsterDensity: 4, environmentDensity: 2 },
];

const corridorSize = { width: 2, height: 2 };

function getRandomRoomSize() {
  return roomSizes[Math.floor(Math.random() * roomSizes.length)];
}

function doesOverlap(newRoom, rooms) {
  return rooms.some((room) => {
    return !(
      newRoom.x + newRoom.width <= room.x ||
      newRoom.x >= room.x + room.width ||
      newRoom.y + newRoom.height <= room.y ||
      newRoom.y >= room.y + room.height
    );
  });
}

function touches(a, b) {
  const xTouch = a.x + a.width === b.x || b.x + b.width === a.x;
  const yOverlap = a.y < b.y + b.height && a.y + a.height > b.y;

  const yTouch = a.y + a.height === b.y || b.y + b.height === a.y;
  const xOverlap = a.x < b.x + b.width && a.x + a.width > b.x;

  return (xTouch && yOverlap) || (yTouch && xOverlap);
}

function isWithinBounds(room) {
  return (
    room.x >= 0 &&
    room.y >= 0 &&
    room.x + room.width <= gridWidth &&
    room.y + room.height <= gridHeight
  );
}

function getOffsetFromDirection(base, dir, size) {
  const { x, y, width, height } = base;
  switch (dir) {
    case "north":
      return { x, y: y - size.height, width: size.width, height: size.height };
    case "south":
      return { x, y: y + height, width: size.width, height: size.height };
    case "west":
      return { x: x - size.width, y, width: size.width, height: size.height };
    case "east":
      return { x: x + width, y, width: size.width, height: size.height };
    default:
      return null;
  }
}

function getRandomOffsetAlongWall(base, dir, size) {
  switch (dir) {
    case "north":
    case "south": {
      const maxX = base.width - size.width;
      const offsetX = base.x + Math.floor(Math.random() * (maxX + 1));
      const y = dir === "north" ? base.y - size.height : base.y + base.height;
      return { x: offsetX, y, width: size.width, height: size.height };
    }
    case "west":
    case "east": {
      const maxY = base.height - size.height;
      const offsetY = base.y + Math.floor(Math.random() * (maxY + 1));
      const x = dir === "west" ? base.x - size.width : base.x + base.width;
      return { x, y: offsetY, width: size.width, height: size.height };
    }
    default:
      return null;
  }
}

// function getOffset(base, dir, size) {
//   switch (dir) {
//     case "north":
//       return {
//         x: base.x,
//         y: base.y - size.height,
//         width: size.width,
//         height: size.height,
//       };
//     case "south":
//       return {
//         x: base.x,
//         y: base.y + base.height,
//         width: size.width,
//         height: size.height,
//       };
//     case "west":
//       return {
//         x: base.x - size.width,
//         y: base.y,
//         width: size.width,
//         height: size.height,
//       };
//     case "east":
//       return {
//         x: base.x + base.width,
//         y: base.y,
//         width: size.width,
//         height: size.height,
//       };
//     default:
//       return null;
//   }
// }

function getRandomSubset(array, min, max) {
  const count = Math.min(
    array.length,
    Math.floor(Math.random() * (max - min + 1)) + min
  );
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateRooms(mapSize) {
  const numRooms =
    mapSize === "Sehr klein"
      ? getRandomInt(2, 3)
      : mapSize === "Klein"
      ? getRandomInt(3, 5)
      : mapSize === "Normal"
      ? getRandomInt(5, 9)
      : mapSize === "Groß"
      ? getRandomInt(9, 14)
      : mapSize === "Sehr groß"
      ? getRandomInt(14, 20)
      : getRandomInt(20, 30);

  const rooms = [];
  const corridors = [];
  let roomCount = 1;

  const startRoomSize = getRandomRoomSize();
  const startRoom = {
    type: "start",
    ...startRoomSize,
    x: Math.floor(gridWidth / 2 - startRoomSize.width / 2),
    y: gridHeight - startRoomSize.height - 1,
    exits: ["south"],
  };

  rooms.push(startRoom);

  let pendingConnections = ["north", "east", "west"].map((dir) => ({
    from: startRoom,
    dir,
  }));

  while (roomCount < numRooms) {
    if (pendingConnections.length === 0) {
      const candidates = rooms.filter((r) => r.exits.length < 4);
      if (candidates.length === 0) break;
      const from = candidates[Math.floor(Math.random() * candidates.length)];
      const dirs = ["north", "south", "east", "west"].filter(
        (d) => !from.exits.includes(d)
      );
      if (dirs.length === 0) break;
      const dir = dirs[Math.floor(Math.random() * dirs.length)];
      pendingConnections.push({ from, dir });
    }

    const { from, dir } = pendingConnections.shift();

    const corridor = getRandomOffsetAlongWall(from, dir, corridorSize);
    const roomSize = getRandomRoomSize();
    const newRoom = getRandomOffsetAlongWall(corridor, dir, roomSize);
    newRoom.monsterDensity = roomSize.monsterDensity;
    if (
      !isWithinBounds(corridor, gridWidth, gridHeight) ||
      !isWithinBounds(newRoom, gridWidth, gridHeight)
    )
      continue;

    if (doesOverlap(corridor, [...rooms, ...corridors])) continue;
    if (doesOverlap(newRoom, [...rooms, ...corridors])) continue;

    corridors.push({ ...corridor, type: "corridor" });

    newRoom.type = "room";
    newRoom.exits = [];

    const possibleExits = ["north", "south", "east", "west"].filter(
      (d) => d !== dir
    );

    const newExits = getRandomSubset(possibleExits, 2, 3); // mehr Verbindungen ermöglichen

    newExits.forEach((exit) => {
      const testCorridor = getOffsetFromDirection(newRoom, exit, corridorSize);
      const testRoomSize = getRandomRoomSize();
      const testRoom = getOffsetFromDirection(testCorridor, exit, testRoomSize);

      if (
        isWithinBounds(testCorridor, gridWidth, gridHeight) &&
        isWithinBounds(testRoom, gridWidth, gridHeight) &&
        !doesOverlap(testCorridor, [...rooms, ...corridors, newRoom]) &&
        !doesOverlap(testRoom, [...rooms, ...corridors, newRoom])
      ) {
        newRoom.exits.push(exit);
        pendingConnections.push({ from: newRoom, dir: exit });
      }
    });

    rooms.push(newRoom);
    roomCount++;
  }

  return [...rooms, ...corridors];
}

function generateMonsters(rooms, difficulty, monsterType, dungeonSize) {
  const factor =
    {
      "Sehr einfach": 0.5,
      Einfach: 1,
      Normal: 2.5,
      Schwer: 4,
      "Sehr schwer": 7,
    }[difficulty] || 1;

  const { monsters: monsterPool = [] } = monsterData[monsterType] || {};
  const encounters = [];

  const limitFactor =
    dungeonSize === "Sehr klein"
      ? 1
      : dungeonSize === "Klein"
      ? 1
      : dungeonSize === "Normal"
      ? 2
      : dungeonSize === "Groß"
      ? 3
      : dungeonSize === "Sehr groß"
      ? 4
      : 5;

  const monsterCounts = {}; // Für die Monsterspezifische Limitierung

  rooms
    .filter((r) => r.type === "room" || r.type === "start")
    .forEach((room) => {
      let cr = 0;
      const maxCR = factor;
      const monsters = [];

      const maxMonsters = room.monsterDensity ?? 4;
      const usedPositions = new Set();

      while (cr < maxCR && monsters.length < maxMonsters) {
        const m = monsterPool[Math.floor(Math.random() * monsterPool.length)];

        // Limitprüfung
        const currentCount = monsterCounts[m.name] || 0;
        if (
          typeof m.limit === "number" &&
          currentCount >= m.limit * limitFactor
        ) {
          console.log(
            "Monsterlimit erreicht - " +
              m.name +
              " " +
              currentCount +
              "x vorhanden."
          );
          continue; // Monsterlimit erreicht
        }

        if (cr + m.cr > maxCR) break;

        let tries = 0;
        let position = null;

        while (tries < 20) {
          const x =
            room.x +
            1 +
            Math.floor(Math.random() * Math.max(1, room.width - 2));
          const y =
            room.y +
            1 +
            Math.floor(Math.random() * Math.max(1, room.height - 2));
          const key = `${x},${y}`;

          if (!usedPositions.has(key)) {
            position = { x, y };
            usedPositions.add(key);
            break;
          }

          tries++;
        }

        if (!position) break; // kein Platz gefunden

        cr += m.cr;
        monsterCounts[m.name] = (monsterCounts[m.name] || 0) + 1;
        //monsters.push({ ...m, position });

        //Loot generieren

        const loot = [];

        (m.lootSources || []).forEach((source) => {
          const table = lootData[source.table];
          if (!table) return;

          const min = source.minItems ?? 0;
          const max = source.maxItems ?? 0;
          const targetCount = getRandomInt(min, max);

          const selected = [];
          const attempts = 100;
          let tries = 0;

          while (selected.length < targetCount && tries < attempts) {
            const item = table[Math.floor(Math.random() * table.length)];
            tries++;

            // Chance check
            if (Math.random() <= item.chance) {
              let quantity = 1;
              if (item.min !== undefined && item.max !== undefined) {
                const rolled = getRandomInt(item.min, item.max);
                const multiplier = parseInt(item.multiplier ?? 1) || 1;
                quantity = rolled * multiplier;
              }

              const totalValue =
                item.value !== undefined
                  ? quantity * parseFloat(item.value)
                  : undefined;

              selected.push({
                item: item.item,
                description: item.description,
                quantity,
                currency: item.currency,
                singleValue: item.value,
                totalValue,
              });
            }
          }

          // Wenn trotz aller Versuche kein Loot zustande kam, aber minItems > 0 → Zwangswahl
          if (selected.length === 0 && min > 0) {
            const fallback = table[Math.floor(Math.random() * table.length)];

            let quantity = 1;
            if (fallback.min !== undefined && fallback.max !== undefined) {
              const rolled = getRandomInt(fallback.min, fallback.max);
              const multiplier = parseInt(fallback.multiplier ?? 1) || 1;
              quantity = rolled * multiplier;
            }

            const totalValue =
              fallback.value !== undefined
                ? quantity * parseFloat(fallback.value)
                : undefined;

            selected.push({
              item: fallback.item,
              description: fallback.description,
              quantity,
              currency: fallback.currency,
              singleValue: fallback.value,
              totalValue,
            });
          }

          loot.push(...selected);
        });

        monsterCounts[m.name] = (monsterCounts[m.name] || 0) + 1;
        monsters.push({ ...m, position, loot });
      }

      encounters.push({ room, monsters });
    });

  return encounters;
}

// function generateEnvironment(rooms, dungeonSize, envSet = "default") {
//   const { objects: envPool = [] } = environmentData[envSet] || {};

//   const encounters = rooms
//     .filter((r) => r.type === "room" || r.type === "start")
//     .map((room) => {
//       // 1) Würfele, wie viele Objekte (0…environmentDensity) wir tatsächlich platzieren wollen
//       const maxItems = room.environmentDensity || 1;
//       const countToPlace = getRandomInt(0, maxItems);

//       // 2) Shuffle den Pool und versuche pro Eintrag genau einmal den Chance-Roll
//       const poolShuffled = [...envPool].sort(() => 0.5 - Math.random());
//       const placed = [];
//       const used = new Set();

//       for (const obj of poolShuffled) {
//         if (placed.length >= countToPlace) break;
//         if (Math.random() > obj.chance) continue; // Chance-Check

//         // 3) Finde eine freie Position im Room
//         let tries = 0,
//           pos = null;
//         while (tries < 20 && !pos) {
//           const x = room.x + 1 + Math.floor(Math.random() * (room.width - 2));
//           const y = room.y + 1 + Math.floor(Math.random() * (room.height - 2));
//           const key = `${x},${y}`;
//           if (!used.has(key)) {
//             pos = { x, y };
//             used.add(key);
//           }
//           tries++;
//         }
//         if (pos) placed.push({ ...obj, position: pos });
//       }

//       return { room, objects: placed };
//     });

//   return encounters;
// }

function generateEnvironment(rooms, encounters, envSet = "default") {
  const { objects: envPool = [] } = environmentData[envSet] || {};

  // 1) Alle Monster-Positionen als belegt markieren
  const used = new Set();
  encounters.forEach((enc) => {
    enc.monsters.forEach((monster) => {
      const key = `${monster.position.x},${monster.position.y}`;
      used.add(key);
    });
  });

  return rooms
    .filter((r) => r.type === "room" || r.type === "start")
    .map((room) => {
      const maxItems = room.environmentDensity || 1;
      const placed = [];

      for (let i = 0; i < maxItems; i++) {
        const obj = envPool[Math.floor(Math.random() * envPool.length)];
        if (Math.random() > obj.chance) continue;

        // 2) Freie Position finden (skippt auch Monster-Zellen dank used-Set)
        let tries = 0,
          pos = null;
        while (tries < 20 && !pos) {
          const x = room.x + 1 + Math.floor(Math.random() * (room.width - 2));
          const y = room.y + 1 + Math.floor(Math.random() * (room.height - 2));
          const key = `${x},${y}`;
          if (!used.has(key)) {
            pos = { x, y };
            used.add(key);
          }
          tries++;
        }
        if (!pos) continue;

        const placedObj = { ...obj, position: pos };
        if (obj.type === "treasure" && Array.isArray(obj.lootSources)) {
          const loot = [];

          obj.lootSources.forEach((source) => {
            const table = lootData[source.table] || [];
            const min = source.minItems ?? 0;
            const max = source.maxItems ?? 0;
            const targetCount = getRandomInt(min, max);

            let placedThis = 0;
            let attempts = 0;
            const sourceLoot = [];

            // pro Quelle nur so oft schleifen, bis ich targetCount aus dieser Quelle habe
            while (placedThis < targetCount && attempts < 100) {
              attempts++;
              const item = table[Math.floor(Math.random() * table.length)];
              if (Math.random() <= (item.chance ?? 1)) {
                // Menge und Wert wie gehabt
                const qty =
                  item.min !== undefined && item.max !== undefined
                    ? getRandomInt(item.min, item.max) *
                      (parseInt(item.multiplier) || 1)
                    : 1;
                const totalValue = item.value
                  ? qty * parseFloat(item.value)
                  : undefined;

                sourceLoot.push({
                  item: item.item,
                  description: item.description,
                  quantity: qty,
                  currency: item.currency,
                  singleValue: item.value,
                  totalValue,
                });
                placedThis++;
              }
            }

            // Fallback pro Quelle
            if (sourceLoot.length === 0 && min > 0 && table.length) {
              const fb = table[Math.floor(Math.random() * table.length)];
              const qty =
                fb.min !== undefined && fb.max !== undefined
                  ? getRandomInt(fb.min, fb.max) *
                    (parseInt(fb.multiplier) || 1)
                  : 1;
              const totalValue = fb.value
                ? qty * parseFloat(fb.value)
                : undefined;
              sourceLoot.push({
                item: fb.item,
                description: fb.description,
                quantity: qty,
                currency: fb.currency,
                singleValue: fb.value,
                totalValue,
              });
            }

            // und erst jetzt an den globalen loot-Pool hängen
            loot.push(...sourceLoot);
          });

          placedObj.loot = loot;
        }
        placed.push(placedObj);
      }

      return { room, objects: placed };
    });
}

function DungeonGenerator() {
  const canvasRef = useRef(null);
  const [numRooms, setNumRooms] = useState("Normal");
  const [difficulty, setDifficulty] = useState("Normal");
  const [monsterType, setMonsterType] = useState("Banditen");
  const [rooms, setRooms] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [log, setLog] = useState([]);
  const [hoveredMonster, setHoveredMonster] = useState(null);
  const [environment, setEnvironment] = useState([]);

  function addLogEntry(entry) {
    setLog((prev) => [...prev, `📝 ${entry}`].slice(-200)); // nur die letzten 200 Einträge
  }

  const handleGenerate = () => {
    //Log-reset
    setLog([]);

    const dungeon = generateRooms(numRooms).map((r) => ({
      ...r,
      visible: false,
      revealed: false,
    }));

    const startRoom = dungeon.find((r) => r.type === "start");
    startRoom.visible = true;
    startRoom.revealed = true;

    dungeon.forEach((r) => {
      if (r.type === "corridor" && touches(r, startRoom)) {
        r.visible = true;
      }
    });

    const monsters = generateMonsters(
      dungeon,
      difficulty,
      monsterType,
      numRooms
    );
    setRooms(dungeon);
    setEncounters(monsters);

    const envEncounters = generateEnvironment(dungeon, monsters);
    setEnvironment(envEncounters);

    // 💡 Debug-Ausgabe
    console.log("🔍 Dungeon:", dungeon);
    console.log("👹 Monster Encounters:", monsters);
  };

  useEffect(() => {
    const canvas = canvasRef.current;

    canvas.onclick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = Math.floor((e.clientX - rect.left) / scale);
      const y = Math.floor((e.clientY - rect.top) / scale);

      //LOOT?

      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // ——— Schatzkisten-Click abfangen ———
      let clickedEnv = null;
      for (const enc of environment) {
        for (const obj of enc.objects) {
          const { x: ox, y: oy } = obj.position;
          const px = ox * scale + scale / 2;
          const py = oy * scale + scale / 2;
          const radius = scale * 0.4;
          const dx = clickX - px;
          const dy = clickY - py;
          if (Math.sqrt(dx * dx + dy * dy) < radius && !obj.collected) {
            clickedEnv = obj;
            break;
          }
        }
        if (clickedEnv) break;
      }

      if (clickedEnv) {
        // Loot-Logbucheintrag
        if (clickedEnv.loot && clickedEnv.loot.length > 0) {
          addLogEntry(`💰 ${clickedEnv.name} enthält:`);
          clickedEnv.loot.forEach((item) => {
            const qty = item.quantity || 1;
            const val =
              item.totalValue && item.currency
                ? ` (Wert: ${item.totalValue} ${item.currency})`
                : "";
            addLogEntry(`${qty}x ${item.item}${val}`);
            addLogEntry(`ℹ️ ${item.description}`);
          });
        } else {
          addLogEntry(`🗝️ Die Schatzkiste ist leer.`);
        }

        // Kiste als geplündert markieren und State aktualisieren
        clickedEnv.collected = true;
        setEnvironment([...environment]);
        return; // verhindert, dass noch Monster oder Räume getriggert werden
      }
      // ——— E

      let clickedMonster = null;
      for (const enc of encounters) {
        for (const monster of enc.monsters) {
          const { x, y } = monster.position;
          const px = x * scale + scale / 2;
          const py = y * scale + scale / 2;
          const radius = scale * 0.3;

          const dx = clickX - px;
          const dy = clickY - py;

          if (Math.sqrt(dx * dx + dy * dy) < radius) {
            clickedMonster = monster;
            break;
          }
        }
        if (clickedMonster) break;
      }

      if (clickedMonster) {
        if (!clickedMonster.loot || clickedMonster.loot.length === 0) {
          addLogEntry(`${clickedMonster.name} trägt nichts bei sich.`);
        } else {
          addLogEntry(`💰 ${clickedMonster.name} hinterlässt folgende Beute.`);
          clickedMonster.loot.forEach((item) => {
            const qty = item.quantity || 1;
            const name = item.item;
            const desc = item.description;
            const val =
              item.totalValue && item.currency
                ? ` (Wert: ${item.totalValue} ${item.currency})`
                : "";
            addLogEntry(`${qty}x ${name}${val}`);
            addLogEntry(`ℹ️ ${desc}`);
          });
        }

        clickedMonster.defeated = true;
        setEncounters([...encounters]); // State aktualisieren, damit neu gezeichnet wird
        return; // Klick nicht weiterverarbeiten (z. B. kein Raumwechsel)
      }

      //LOOT ENDE

      const clicked = rooms.find(
        (r) =>
          r.visible &&
          r.type === "corridor" &&
          x >= r.x &&
          x < r.x + r.width &&
          y >= r.y &&
          y < r.y + r.height
      );

      if (clicked) {
        addLogEntry(`Du hast einen neuen Raum betreten.`);
        const newlyVisible = rooms.filter(
          (r) => r.type === "room" && !r.visible && touches(r, clicked)
        );

        newlyVisible.forEach((r) => {
          r.visible = true;

          const encounter = encounters.find(
            (enc) => enc.room.x === r.x && enc.room.y === r.y
          );

          if (encounter) {
            const countByName = {};
            encounter.monsters.forEach((m) => {
              countByName[m.name] = (countByName[m.name] || 0) + 1;
            });

            const summary = Object.entries(countByName)
              .map(([name, count]) => `${count}x ${name}`)
              .join(", ");

            if (encounter.monsters.length > 0) {
              addLogEntry(`👹 ${summary} im Raum`);
            } else {
              addLogEntry(`👹 Es befinden sich keine Gegner im Raum`);
            }
          }
        });

        rooms.forEach((r) => {
          if (
            r.type === "corridor" &&
            !r.visible &&
            newlyVisible.some((n) => touches(n, r))
          ) {
            r.visible = true;
          }
        });

        setRooms([...rooms]);
      }
    };

    canvas.onmousemove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      let found = null;

      for (const enc of encounters) {
        for (const monster of enc.monsters) {
          const { x, y } = monster.position;
          const px = x * scale + scale / 2;
          const py = y * scale + scale / 2;
          const radius = scale * 0.3;

          const dx = mouseX - px;
          const dy = mouseY - py;

          if (Math.sqrt(dx * dx + dy * dy) < radius) {
            found = {
              name: monster.name,
              cr: monster.cr,
              ac: monster.ac,
              speed: monster.speed,
              hit: monster.hit,
              damage: monster.damage,
              monsterManual: monster.monsterManualPage,
              hitPoints: monster.hitPoints,
              x: mouseX + 10,
              y: mouseY + 60,
              loot: monster.loot ?? [],
            };
            break;
          }
        }
        if (found) break;
      }

      // 2) Wenn kein Monster, prüfe Environment
      if (!found) {
        for (const enc of environment) {
          for (const obj of enc.objects) {
            const px = obj.position.x * scale + scale / 2;
            const py = obj.position.y * scale + scale / 2;
            const dx = mouseX - px;
            const dy = mouseY - py;
            const radius = scale * 0.3;
            if (Math.sqrt(dx * dx + dy * dy) < radius && !obj.collected) {
              found = {
                name: obj.name,
                x: mouseX + 10,
                y: mouseY + 60,
                isEnv: true,
              };
              break;
            }
          }
          if (found) break;
        }
      }

      setHoveredMonster(found);
    };

    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

    // draw rooms
    rooms.forEach((r) => {
      if (!r.visible) return;

      ctx.fillStyle =
        r.type === "start"
          ? "green"
          : r.type === "corridor"
          ? "gray"
          : "#284e73";

      ctx.fillRect(r.x * scale, r.y * scale, r.width * scale, r.height * scale);

      //UMRANDUNG DER EINZELNEN RÄUME
      ctx.strokeStyle = "#89e9f0";
      ctx.lineWidth = 1; // Dünne Linie
      ctx.strokeRect(
        r.x * scale,
        r.y * scale,
        r.width * scale,
        r.height * scale
      );
    });

    // draw monsters
    encounters.forEach((enc) => {
      const room = rooms.find(
        (r) => r.x === enc.room.x && r.y === enc.room.y && r.visible
      );

      if (!room) return;

      enc.monsters.forEach((monster) => {
        const { x, y } = monster.position;
        const px = x * scale + scale / 2;
        const py = y * scale + scale / 2;
        const radius = scale * 0.3;

        ctx.beginPath();
        ctx.arc(px, py, radius, 0, 2 * Math.PI);
        ctx.fillStyle = monster.defeated ? " #755f4a" : " #FF6961";
        ctx.fill();

        ctx.fillStyle = "white";
        ctx.font = `${scale * 0.4}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(monster.name[0], px, py);
      });
    });

    // draw environment
    environment.forEach((enc) => {
      const room = rooms.find(
        (r) => r.x === enc.room.x && r.y === enc.room.y && r.visible
      );
      if (!room) return;
      enc.objects.forEach((obj) => {
        const px = obj.position.x * scale + scale / 2;
        const py = obj.position.y * scale + scale / 2;

        if (obj.collected) {
          ctx.fillStyle = "#755f4a";
        } else if (obj.type === "treasure") {
          ctx.fillStyle = "#FFD700"; // gelb für ungenutzte Kisten
        } else if (obj.type === "trap") {
          ctx.fillStyle = "darkred"; // Fallen
        } else {
          ctx.fillStyle = "gray"; // Fallback
        }

        ctx.beginPath();
        ctx.rect(px - scale * 0.3, py - scale * 0.3, scale * 0.6, scale * 0.6);
        ctx.fill();
        // ggf. erster Buchstabe
        ctx.fillStyle = "black";
        ctx.fillText(obj.name[0], px, py);
      });
    });

    // draw grid
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    for (let i = 0; i <= gridWidth; i++) {
      const x = i * scale;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvasSize.height);
      ctx.stroke();
    }
    for (let j = 0; j <= gridHeight; j++) {
      const y = j * scale;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvasSize.width, y);
      ctx.stroke();
    }
  }, [rooms, encounters, environment]);

  return (
    <div
      className="p-4"
      style={{
        padding: "10px",
        backgroundColor: "#848482",
        height: "calc(100vh - 20px)",
      }}
    >
      <h1 className="text-xl font-bold mb-2"> 🐉 Arksol's Dungeon Generator 🐉</h1>
      <div className="mb-4 flex items-center gap-2">
        <label style={{ marginRight: "0.5rem" }}>Größe:</label>
        <select
          style={{ marginRight: "1.5rem", backgroundColor: "#a4a4a3" }}
          value={numRooms}
          onChange={(e) => setNumRooms(e.target.value)}
          className="border px-2 py-1"
        >
          {["Sehr klein", "Klein", "Normal", "Groß", "Sehr groß", "Riesig"].map(
            (value) => (
              <option key={value} value={value}>
                {value}
              </option>
            )
          )}
        </select>

        <label style={{ marginRight: "0.5rem" }}>Schwierigkeitsgrad:</label>
        <select
          style={{ marginRight: "0.5rem", backgroundColor: "#a4a4a3" }}
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
        >
          <option>Sehr einfach</option>
          <option>Einfach</option>
          <option>Normal</option>
          <option>Schwer</option>
          <option>Sehr schwer</option>
        </select>

        <label style={{ marginRight: "0.5rem" }}>Monster-Set:</label>
        <select
          style={{ marginRight: "1rem", backgroundColor: "#a4a4a3" }}
          value={monsterType}
          onChange={(e) => setMonsterType(e.target.value)}
        >
          {Object.keys(monsterData)
            .filter((key) => key !== "globalLootTables") // optional, falls du globale Tabellen ausschließen willst
            .map((set) => (
              <option key={set} value={set}>
                {set}
              </option>
            ))}
        </select>

        <button
          onClick={handleGenerate}
          style={{backgroundColor: "#a4a4a3", height: "2rem", marginBottom: "0.25rem"}}
          className="bg-blue-500 text-white px-4 py-1 rounded"
        >
          Generieren
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          style={{ border: "2px solid #a8a8a7", backgroundColor: "black" }}
        />

        <div
          style={{ marginLeft: "0.75rem" }}
          className="mt-4 bg-gray-900 text-white p-2 rounded max-h-40 overflow-y-auto text-sm"
        >
          <h2 className="font-bold mb-1">📕 Log</h2>
          <ul
            style={{
              height: "50vh",
              maxHeight: "50vh",
              width: "25rem",
              border: "1px solid black",
              backgroundColor: "#a4a4a3",
              overflowY: "scroll",
            }}
            className="list-disc ml-5 space-y-1"
          >
            {log.map((entry, idx) => (
              <li style={{ padding: "3px" }} key={idx}>
                {entry}
              </li>
            ))}
          </ul>
          <button
            onClick={() => setLog([])}
            style={{ backgroundColor: "#a4a4a3", height: "2.5rem" }}
            className="bg-red-500 text-white px-2 py-1 rounded"
          >
            🗑️ Logs löschen
          </button>
        </div>
      </div>

      {hoveredMonster && (
        <div
          style={{
            position: "absolute",
            left: hoveredMonster.x,
            top: hoveredMonster.y,
            background: "rgba(0,0,0,0.8)",
            border: "1px solid white",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "0.8rem",
            pointerEvents: "auto",
            zIndex: 10,
            cursor: "pointer",
          }}
          onClick={() => {
            if (hoveredMonster.loot.length === 0) {
              addLogEntry(`${hoveredMonster.name} trägt nichts bei sich.`);
            } else {
              addLogEntry(
                `💰 ${hoveredMonster.name} hinterlässt folgende Beute.`
              );
              hoveredMonster.loot.forEach((item) => {
                const qty = item.quantity || 1;
                const name = item.item;
                const desc = item.description;
                const val =
                  item.totalValue && item.currency
                    ? ` (Wert: ${item.totalValue} ${item.currency})`
                    : "";
                addLogEntry(`💰 ${qty}x ${name}${val}`);
                addLogEntry(`ℹ️ ${desc}`);
              });
            }
          }}
        >
          {!hoveredMonster.isEnv ? (
            // nur Name für environment

            <>
              <div style={{ fontSize: "1.25rem" }}>{hoveredMonster.name}</div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                HP: {hoveredMonster.hitPoints}
              </div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                CR: {hoveredMonster.cr}
              </div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                Rüstungsklasse: {hoveredMonster.ac}
              </div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                Bewegung: {hoveredMonster.speed}
              </div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                Waffe: +{hoveredMonster.hit}
              </div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                Schaden: {hoveredMonster.damage}
              </div>
              <div style={{ fontSize: "1rem", color: "#aaa" }}>
                Monsterhandbuch: Seite {hoveredMonster.monsterManual}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: "1.25rem" }}>{hoveredMonster.name}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DungeonGenerator;
